import { useEffect, useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import { fetchDevicePosture } from '../api/client';
import Badge from './ui/Badge';
import { complianceStateTone } from './ui/badgeTones';

// Local copy of CollapsibleSection (same idiom as AlertsPage / TestRunResultsPage)
// keeps this panel self-contained — parents just drop it into the expanded row.
function CollapsibleSection({ title, titleClass = 'text-gray-400', defaultCollapsed = true, children }) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  return (
    <div>
      <div
        className="flex items-center gap-1.5 cursor-pointer select-none"
        onClick={(e) => { e.stopPropagation(); setCollapsed(c => !c); }}
      >
        {collapsed
          ? <ChevronRightIcon className="h-3 w-3 text-gray-500 flex-shrink-0" />
          : <ChevronDownIcon className="h-3 w-3 text-gray-500 flex-shrink-0" />}
        <span className={`text-xs ${titleClass} font-semibold uppercase tracking-wide`}>{title}</span>
      </div>
      {!collapsed && <div className="mt-2">{children}</div>}
    </div>
  );
}

function Field({ label, children, mono = false }) {
  return (
    <div className="flex items-start gap-2">
      <span className="text-gray-500 shrink-0 w-32">{label}</span>
      <span className={`text-gray-300 break-all ${mono ? 'font-mono' : ''}`}>
        {children == null || children === '' ? <span className="text-gray-600 italic">—</span> : children}
      </span>
    </div>
  );
}

function BoolField({ label, value }) {
  if (value == null) {
    return <Field label={label}><span className="text-gray-600 italic">unknown</span></Field>;
  }
  return (
    <Field label={label}>
      <span className={value ? 'text-green-400' : 'text-amber-400'}>{value ? 'Yes' : 'No'}</span>
    </Field>
  );
}

function fmtDate(s) {
  if (!s) return '';
  // Most values come ISO 8601; render as local date+time. Z-naive handled by JS Date best-effort.
  try {
    const d = new Date(s);
    if (isNaN(d.getTime())) return s;
    return d.toLocaleString();
  } catch {
    return s;
  }
}

/**
 * DevicePosturePanel
 *
 * Resolves an alert's deviceDetail.deviceId against the daily snapshots in
 * SilverM365EntraDevices + SilverM365IntuneDevices and shows what the directory
 * + MDM know about the device.
 *
 * The panel is collapsed by default. On expand it lazy-fetches `/api/alerts/device-posture`.
 * Three render branches:
 *   - Entra hit + Intune hit  → side-by-side directory + MDM blocks
 *   - Entra hit + Intune miss → directory block only (with note for non-Intune tenants)
 *   - Entra miss              → "device unknown to the directory" warning
 */
export default function DevicePosturePanel({ tenantId, deviceId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);

  useEffect(() => {
    if (!tenantId || !deviceId || hasFetched) return;
    setLoading(true);
    fetchDevicePosture(tenantId, deviceId)
      .then(d => { setData(d); setError(null); })
      .catch(e => setError(e?.message || 'Failed to load device posture'))
      .finally(() => { setLoading(false); setHasFetched(true); });
  }, [tenantId, deviceId, hasFetched]);

  if (!tenantId || !deviceId) return null;

  const entra = data?.entra || null;
  const intune = data?.intune || null;
  const entraHit = !!entra;
  const intuneHit = !!intune;

  return (
    <CollapsibleSection title="Device Posture (Directory + MDM)" titleClass="text-cyan-400">
      {loading && <p className="text-gray-500 italic">Loading device posture…</p>}
      {error && <p className="text-red-400">{error}</p>}
      {!loading && !error && data && (
        <div>
          {!entraHit && !intuneHit && (
            <div className="rounded border border-amber-700/40 bg-amber-900/20 px-3 py-2 text-amber-300">
              Device unknown to the directory — likely AiTM/proxy session or unregistered BYOD.
              <span className="block text-amber-400/70 text-[10px] mt-1 font-mono">
                lookup: {tenantId} / {deviceId}
              </span>
            </div>
          )}

          {entraHit && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
              {/* Entra block */}
              <div>
                <div className="text-[11px] text-cyan-400 mb-2 font-semibold uppercase tracking-wide">
                  Entra (Directory)
                </div>
                <div className="space-y-1">
                  <Field label="Display name">{entra.displayName}</Field>
                  <Field label="Trust type">{entra.trustType}</Field>
                  <BoolField label="Account enabled" value={entra.accountEnabled} />
                  <Field label="Last sign-in">{fmtDate(entra.approximateLastSignInDateTime)}</Field>
                  <Field label="Ownership">{entra.deviceOwnership}</Field>
                  <Field label="Operating system">
                    {entra.operatingSystem}{entra.operatingSystemVersion ? ` ${entra.operatingSystemVersion}` : ''}
                  </Field>
                  <Field label="Profile type">{entra.profileType}</Field>
                  <Field label="Registered">{fmtDate(entra.registrationDateTime)}</Field>
                  <Field label="Snapshot date">{entra.collectionDate}</Field>
                </div>
              </div>

              {/* Intune block */}
              <div>
                <div className="text-[11px] text-cyan-400 mb-2 font-semibold uppercase tracking-wide">
                  Intune (MDM)
                </div>
                {intuneHit ? (
                  <div className="space-y-1">
                    <Field label="Compliance state">
                      {intune.complianceState
                        ? <Badge tone={complianceStateTone(intune.complianceState)}>{intune.complianceState}</Badge>
                        : <span className="text-gray-600 italic">—</span>}
                    </Field>
                    <Field label="Primary user">{intune.userPrincipalName}</Field>
                    <Field label="Device name">{intune.managedDeviceName || intune.deviceName}</Field>
                    <Field label="Owner type">{intune.managedDeviceOwnerType}</Field>
                    <Field label="OS version">{intune.osVersion}</Field>
                    <Field label="Last sync">{fmtDate(intune.lastSyncDateTime)}</Field>
                    <Field label="Enrolled">{fmtDate(intune.enrolledDateTime)}</Field>
                    <BoolField label="Encrypted" value={intune.isEncrypted} />
                    <Field label="Jail-broken">{intune.jailBroken}</Field>
                    <Field label="Threat state">{intune.partnerReportedThreatState}</Field>
                    <Field label="Snapshot date">{intune.collectionDate}</Field>
                  </div>
                ) : (
                  <div className="rounded border border-amber-700/40 bg-amber-900/20 px-3 py-2 text-amber-300">
                    Device is registered in Entra but has no Intune row.
                    <span className="block text-amber-400/70 text-[10px] mt-1">
                      Either the user is not enrolled in Intune, or this tenant is not yet on the
                      DeviceManagementManagedDevices.Read.All re-consent — both are valid states.
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </CollapsibleSection>
  );
}
